import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { IconButton, Stack, Typography, Box } from '@mui/material';
import { InputText } from 'app/components/elements/InputText';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { ButtonCustom, ButtonOutLine } from 'app/components/elements/ButtonCustom';
import { useEffect, useState } from 'react';
import { AssetTypeForm, AssetTypes, DropdownOption } from '../../../type';
import { yupResolver } from '@hookform/resolvers/yup';
import { AssetTypesFormSchema } from 'utils/validate';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import { InputSelect } from 'app/components/elements/InputSelect';
import { SelectChangeEvent } from '@mui/material/Select';
import { OPTION_CATEGORY } from '../../../const';
import { InputAutoComplete } from '../../elements/InputAutoComplete';
import { TextError } from '../../../../../components/elements/TextError';
import { getDropdownOptions } from '../../../../../../services/asset';

type ModalAddNewModelProp = {
  isEdit?: boolean;
  isOpen: boolean;
  initData?: AssetTypes;
  onCloseModal: () => void;
  onSubmit: (value: AssetTypeForm) => void;
};

const INPUT_TYPE_OPTIONS = [
  { value: 'TEXT', label: 'Text' },
  { value: 'DROPDOWN', label: 'Dropdown' }
];

export const ModalAddNewTypes = ({
  isOpen,
  onCloseModal,
  onSubmit,
  isEdit,
  initData
}: ModalAddNewModelProp) => {
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>([]);

  const {
    handleSubmit,
    register,
    control,
    setValue,
    reset,
    watch,
    formState: { errors }
  } = useForm<AssetTypeForm>({
    resolver: yupResolver(AssetTypesFormSchema)
  });
  useEffect(() => {
    if (!isOpen) {
      reset({
        name: '',
        fields: []
      });
    }
  }, [isOpen, reset]);
  useEffect(() => {
    if (initData) {
      const formInit: AssetTypeForm = {
        name: initData.name,
        fields: initData.assetEntries.map((item) => {
          // Determine input type based on whether field has dropdown option
          const hasDropdown = !!item.field.dropdownValueOption?.id;
          return {
            ...item.field,
            fieldId: item.field.id,
            dropdownOptionId: item.field.dropdownValueOption?.id,
            dropdownValues: undefined,
            inputType: hasDropdown ? 'DROPDOWN' : 'TEXT',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            defaultValue: (item.field as any).defaultValue || '',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            defaultDropdownValueId: (item.field as any).defaultDropdownValueId || null
          };
        })
      };
      reset(formInit);
    }
  }, [initData, reset]);

  // Fetch dropdown options
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const response = await getDropdownOptions('ASSET_TYPE');
        if (response.success) {
          const data = response.data;
          data.push({ name: 'UnSelect', id: '' });
          setDropdownOptions(data);
        }
      } catch (error) {
        console.error('Failed to fetch dropdown options:', error);
      }
    };

    if (isOpen) {
      fetchDropdownOptions();
    }
  }, [isOpen]);

  const {
    fields: fields,
    append,
    update,
    remove
  } = useFieldArray({
    control,
    name: 'fields'
  });

  // Watch all fields for input type changes
  const watchedFields = watch('fields');

  // Get dropdown values for a specific dropdown option
  const getDropdownValuesForOption = (dropdownOptionId: number | null | undefined) => {
    if (!dropdownOptionId) return [];
    const option = dropdownOptions.find((opt) => opt.id === Number(dropdownOptionId));
    return option?.dropdownValues || [];
  };

  return (
    <DialogConfirm
      maxWidth={480}
      textTitle={isEdit ? 'Edit Asset Type' : 'Add New Asset Type'}
      isOpen={isOpen}
      onClose={onCloseModal}>
      <Stack width={'100%'} alignItems={'flex-end'}>
        <Stack width={'100%'} flexDirection={'row'} alignItems={'flex-start'} gap={'20px'}>
          <Stack style={{ width: '100%', gap: '20px' }}>
            <Stack gap={1.25}>
              <TextTitle>Asset Type</TextTitle>
              <InputText
                colorTitle={'#226AAA'}
                inputError={errors.name?.message}
                placeholder={'Enter name'}
                inputProps={{
                  ...register('name'),
                  style: { minHeight: '46px', height: '46px' }
                }}
              />
            </Stack>
            <Stack gap={1.25}>
              <TextTitle>Entry Fields</TextTitle>
              {fields.map((_, index) => {
                if (fields[index].isDelete) return;
                const currentInputType = watchedFields?.[index]?.inputType || 'TEXT';
                const currentDropdownOptionId = watchedFields?.[index]?.dropdownOptionId;

                return (
                  <Stack
                    pt={index === 0 ? '0' : '20px'}
                    pb={'26px'}
                    style={{ borderBottom: '1px solid #D9D9D9' }}
                    key={index}
                    flexDirection={'row'}
                    alignItems={'flex-start'}
                    gap={1}
                    width={'100%'}>
                    <Stack gap={2} width={'100%'}>
                      <Controller
                        name={`fields.${index}.name`}
                        control={control}
                        render={({ field: { value } }) => {
                          return (
                            <InputAutoComplete
                              placeholder={'Field Name'}
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              onChange={(fieldData: any) => {
                                const { name, unit, category, id, dropdownValueOptionId } =
                                  fieldData;

                                setValue(`fields.${index}.fieldId`, id);
                                setValue(`fields.${index}.name`, name || '');
                                setValue(`fields.${index}.unit`, unit || '');
                                setValue(`fields.${index}.category`, category || '');
                                setValue(`fields.${index}.dropdownOptionId`, dropdownValueOptionId);
                                // Set input type based on whether field has dropdown
                                setValue(
                                  `fields.${index}.inputType`,
                                  dropdownValueOptionId ? 'DROPDOWN' : 'TEXT'
                                );
                              }}
                              value={value ?? ''}
                            />
                          );
                        }}
                      />
                      <InputText
                        colorTitle={'#226AAA'}
                        inputError={errors.fields?.[index]?.unit?.message}
                        placeholder={'Unit'}
                        inputProps={{
                          ...register(`fields.${index}.unit`),
                          style: { minHeight: '46px', height: '46px' },
                          disabled: !!watch(`fields.${index}.fieldId`)
                        }}
                      />
                      <Controller
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <InputSelect
                            inputError={errors.fields?.[index]?.category?.message || ''}
                            value={value}
                            placeholder={'Select Category'}
                            onChange={(e: SelectChangeEvent<unknown>) => {
                              onChange(e.target.value);
                            }}
                            disabled={!!watch(`fields.${index}.fieldId`)}
                            sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                            options={OPTION_CATEGORY}
                          />
                        )}
                        name={`fields.${index}.category`}
                      />

                      {/* Input Type Selector */}
                      <Stack gap={1}>
                        <Controller
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <InputSelect
                              value={value || 'TEXT'}
                              placeholder="Select Input Type"
                              onChange={(e: SelectChangeEvent<unknown>) => {
                                const newType = e.target.value as 'TEXT' | 'DROPDOWN';
                                onChange(newType);

                                // Clear related fields when type changes
                                if (newType === 'TEXT') {
                                  setValue(`fields.${index}.dropdownOptionId`, null);
                                  setValue(`fields.${index}.defaultDropdownValueId`, null);
                                } else {
                                  setValue(`fields.${index}.defaultValue`, '');
                                }
                              }}
                              sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                              options={INPUT_TYPE_OPTIONS}
                            />
                          )}
                          name={`fields.${index}.inputType`}
                        />
                      </Stack>

                      {/* Conditional rendering based on input type */}
                      {currentInputType === 'DROPDOWN' && (
                        <>
                          {/* Dropdown Option Selector */}
                          <Stack gap={1}>
                            <Controller
                              control={control}
                              render={({ field: { onChange, value } }) => {
                                return (
                                  <InputSelect
                                    value={value ? value.toString() : ''}
                                    placeholder="Select Dropdown Option"
                                    onChange={(e: SelectChangeEvent<unknown>) => {
                                      onChange(e.target.value);

                                      // Reset default dropdown value when dropdown option changes
                                      setValue(`fields.${index}.defaultDropdownValueId`, null);

                                      if (e.target.value === '') {
                                        setValue(
                                          `fields.${index}.deleteDropdownId`,
                                          initData?.assetEntries[index]?.field
                                            ?.dropdownValueOptionId
                                        );
                                      }
                                    }}
                                    options={dropdownOptions.map((option) => ({
                                      value: option.id.toString(),
                                      label: option.name,
                                      tooltip:
                                        option.dropdownValues &&
                                        option.dropdownValues.length > 0 ? (
                                          <Box>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontWeight: 'bold',
                                                display: 'block',
                                                mb: 0.5
                                              }}>
                                              Available options:
                                            </Typography>
                                            {option.dropdownValues.map((val) => (
                                              <Typography
                                                key={val.id}
                                                variant="caption"
                                                sx={{ display: 'block' }}>
                                                • {val.value}
                                              </Typography>
                                            ))}
                                          </Box>
                                        ) : undefined
                                    }))}
                                    sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                                  />
                                );
                              }}
                              name={`fields.${index}.dropdownOptionId`}
                            />
                          </Stack>

                          {/* Default Dropdown Value Selector */}
                          {currentDropdownOptionId && (
                            <Stack gap={1}>
                              <Controller
                                control={control}
                                render={({ field: { onChange, value } }) => {
                                  const dropdownValues = getDropdownValuesForOption(
                                    Number(currentDropdownOptionId)
                                  );
                                  return (
                                    <InputSelect
                                      value={value ? value.toString() : ''}
                                      placeholder="Select Default Value (Optional)"
                                      onChange={(e: SelectChangeEvent<unknown>) => {
                                        const val = e.target.value;
                                        onChange(val === '' ? null : Number(val));
                                      }}
                                      options={[
                                        { value: '', label: 'No Default' },
                                        ...dropdownValues.map((dv) => ({
                                          value: dv.id.toString(),
                                          label: dv.value || dv.name
                                        }))
                                      ]}
                                      sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                                    />
                                  );
                                }}
                                name={`fields.${index}.defaultDropdownValueId`}
                              />
                            </Stack>
                          )}
                        </>
                      )}

                      {/* Text Default Value Input */}
                      {currentInputType === 'TEXT' && (
                        <Stack gap={1}>
                          <InputText
                            colorTitle={'#226AAA'}
                            placeholder={'Enter default value (optional)'}
                            inputProps={{
                              ...register(`fields.${index}.defaultValue`),
                              style: { minHeight: '46px', height: '46px' }
                            }}
                          />
                        </Stack>
                      )}
                    </Stack>
                    <IconButton
                      onClick={() => {
                        if (fields[index]?.fieldId) {
                          update(index, { ...fields[index], isDelete: true });
                        } else {
                          remove(index);
                        }
                      }}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                );
              })}
            </Stack>
            <Stack alignItems={'flex-end'}>
              {errors.fields?.root?.message && (
                <TextError errorText={errors.fields?.root?.message} />
              )}
            </Stack>
            <Stack width={'100%'} alignItems={'flex-end'}>
              <ButtonCustom
                label={'Add Another Entry Field'}
                style={{ width: '260px' }}
                onClick={() => {
                  append({
                    name: '',
                    unit: '',
                    category: '',
                    inputType: 'TEXT'
                  });
                }}
              />
            </Stack>
          </Stack>
        </Stack>
        <Stack
          width={'100%'}
          mt={'30px'}
          alignItems={'center'}
          justifyContent={'center'}
          flexDirection={'row'}
          gap={'20px'}>
          <ButtonOutLine
            label={'Cancel'}
            style={{ width: `calc((100% - 20px)/2)` }}
            onClick={onCloseModal}
            fullWidth
          />
          <ButtonCustom
            label={isEdit ? 'Save' : 'Add'}
            style={{ width: `calc((100% - 20px)/2)` }}
            onClick={handleSubmit((data) => {
              onSubmit({
                ...data,
                fields: data.fields?.map((item) => ({
                  ...item,
                  dropdownOptionId:
                    (item.dropdownOptionId as unknown) === '' ? null : item.dropdownOptionId,
                  defaultDropdownValueId:
                    item.inputType === 'DROPDOWN' ? item.defaultDropdownValueId : null,
                  defaultValue: item.inputType === 'TEXT' ? item.defaultValue : undefined
                }))
              });
            })}
            fullWidth
          />
        </Stack>
      </Stack>
    </DialogConfirm>
  );
};
const TextTitle = styled(Typography)(() => ({
  fontSize: '18px',
  fontWeight: '800',
  color: '#226AAA'
}));
